import { TURNSTILE_SITE_KEY } from "./config";

interface TurnstileApi {
  render: (
    el: HTMLElement,
    opts: {
      sitekey: string;
      callback: (token: string) => void;
      "error-callback"?: (code: string) => void;
      "expired-callback"?: () => void;
      appearance?: "always" | "execute" | "interaction-only";
    },
  ) => string;
  remove: (widgetId: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let scriptPromise: Promise<void> | null = null;

function loadScript(): Promise<void> {
  if (window.turnstile) return Promise.resolve();
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const s = document.createElement("script");
      s.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
      s.async = true;
      s.onload = () => resolve();
      s.onerror = () => reject(new Error("Failed to load the bot-check script"));
      document.head.appendChild(s);
    });
  }
  return scriptPromise;
}

/**
 * Resolves with a Turnstile token, or undefined when no site key is configured (dev).
 * The widget renders into `container` (invisible/managed mode is configured in the Cloudflare dashboard).
 */
export async function getTurnstileToken(container: HTMLElement): Promise<string | undefined> {
  if (!TURNSTILE_SITE_KEY) return undefined;
  await loadScript();
  const ts = window.turnstile;
  if (!ts) throw new Error("Bot check unavailable");
  const siteKey = TURNSTILE_SITE_KEY;
  return new Promise<string>((resolve, reject) => {
    let id: string | undefined;
    const cleanup = () => {
      if (id) {
        try {
          ts.remove(id);
        } catch {
          /* ignore */
        }
      }
    };
    id = ts.render(container, {
      sitekey: siteKey,
      appearance: "interaction-only",
      callback: (token) => {
        cleanup();
        resolve(token);
      },
      "error-callback": (code) => {
        cleanup();
        reject(new Error(`Bot check failed (${code})`));
      },
      "expired-callback": () => {
        cleanup();
        reject(new Error("Bot check expired, please retry"));
      },
    });
  });
}
